import { authedFetch } from "@/lib/authed-fetch";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2, Folder, FileIcon, ChevronRight, AlertTriangle, FolderOpen,
  Copy, Link2, Lock, Unlock, Trash2, RefreshCw, Plus, Eye, CheckCircle2, XCircle, Clock, Pencil, Info,
  GripVertical, Ungroup, History, MessageCircle, Download, FileArchive, Share2, Ban, Paperclip, Send,
} from "lucide-react";
import { classifyPathOrigin, classifyFolderName, ORIGIN_LABEL_DISPLAY, isDeliveryFolder, type OriginLabel } from "@shared/preview-folders";
import { cleanFileTitle } from "@shared/preview-file-title";
import { detectFileFormat, isDownloadOnlyFormat } from "@shared/file-format";
import {
  isDownloadDisabledPreviewCandidate, isDownloadDisabledPreviewCompatible, SHARE_BROWSER_PREVIEW_MAX_BYTES,
  type DownloadDisabledPreviewFile, type DownloadPolicyPreflightFile, type MediaPreviewVerification,
} from "@shared/share-download-policy";
import { useDownloadPolicyPreflight } from "@/hooks/use-download-policy-preflight";
import { MailOutDialog } from "@/components/mail-out-dialog";
import { buildShareEmailDraft } from "@/lib/share-email-draft";
import {
  confirmUnnamedGroupCreation,
  UNNAMED_GROUP_CONFIRMATION,
} from "@/lib/group-name-confirmation";

export { confirmUnnamedGroupCreation, UNNAMED_GROUP_CONFIRMATION };

/**
 * Preview share dialog (task #29).
 *
 * Two modes:
 * - CREATE (projectId set, no pulseId): browse the project's Dropbox
 *   folder (auto-navigated), multi-select files, set title / password /
 *   expiry, create the share (+ its Preview pulse).
 * - MANAGE (pulseId set): copy link, edit expiry/password, add/remove
 *   files, watch transfer status, retry failures, revoke.
 */

interface DropboxEntry {
  type: "file" | "folder";
  name: string;
  path: string;
  size?: number;
}

interface ShareFileInfo {
  id: number;
  fileName: string;
  title: string;
  description: string | null;
  dropboxPath: string;
  originLabel: OriginLabel;
  sizeBytes: number;
  deliveryMode: string;
  transferStatus: string;
  previewStatus?: string | null;
  hasPreview?: boolean;
  /** Small derived thumbnail exists (generated once at transfer time). */
  hasThumb?: boolean;
  thumbStatus?: 'pending' | 'completed' | 'failed' | null;
  mimeType?: string | null;
  videoCodec?: string | null;
  audioCodec?: string | null;
  mediaPreviewVerification?: MediaPreviewVerification | null;
  transferError: string | null;
  /** Format group: files with the same key are one content in several formats. */
  groupKey: number | null;
  /** 'same_content' (one asset, several encodings) or 'related' (logical bundle). */
  groupKind?: 'same_content' | 'related' | null;
  /** Optional custom group title shown instead of the default label. */
  groupTitle?: string | null;
}

interface ShareInfo {
  id: number;
  pulseId: number;
  projectId: string;
  title: string;
  recipientContactId: string | null;
  urlPath: string | null;
  hasPassword: boolean;
  expiresAt: string;
  revokedAt: string | null;
  files: ShareFileInfo[];
  /** White-label re-sharing (client branding), PM opt-in. */
  allowClientBranding?: boolean;
  /** Delivery-file downloads only; chat attachments are unaffected. */
  disableDownloads?: boolean;
  enableClientUploads?: boolean;
  /** Client-created white-label links derived from this share (manage view). */
  children?: ChildShareInfo[];
  /** 'preview' or 'final' — distinguishes share type (task #189). */
  shareType?: 'preview' | 'final';
  /** FINAL-only opt-in for the same-client project gallery. */
  showClientProjects?: boolean;
}

interface ChildShareInfo {
  id: number;
  createdAt: string;
  revokedAt: string | null;
  /** Accurate external opens (not the frozen legacy viewCount). */
  externalVisitCount: number;
  visitsCountedSince: string | null;
  hasPassword: boolean;
  showChat: boolean;
  showTeam: boolean;
  showDates: boolean;
  files: Array<{ id: number; fileName: string; title: string }>;
}

interface ContactPersonOption {
  id: string;
  firstName: string;
  lastName: string;
}

/** Project IDs are 'YY-CCNN' (e.g. 26-WF01) — the client id is CC. */
function clientIdFromProjectId(projectId?: string): string | undefined {
  const m = projectId?.match(/^\d{2}-([A-Za-z0-9]{2})/);
  return m ? m[1] : undefined;
}

interface PreviewShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId?: string;   // create mode
  pulseId?: number;     // manage mode
  /** Task #189: which share type to create. Defaults to 'preview'. */
  requestedShareType?: 'preview' | 'final';
  /** Display name of the logged-in user who created/sends this share. */
  senderName?: string;
}

/** Task #189: active Final share summary from the backend. */
interface ActiveFinalShareSummary {
  id: number;
  pulseId: number;
  title: string;
  expiresAt: string;
  files: Array<{ id: number; fileName: string; title: string; dropboxPath: string; sizeBytes: number; deliveryMode?: string; previewStatus?: string | null; hasThumb?: boolean; thumbStatus?: 'pending' | 'completed' | 'failed' | null; hasPreview?: boolean; mimeType?: string | null; videoCodec?: string | null; audioCodec?: string | null }>;
}

function inspectionOutcome(
  file: DownloadDisabledPreviewFile,
  result: DownloadPolicyPreflightFile | undefined,
  inspection: { pending: boolean; error: string | null },
): { compatible: boolean; reason: string } {
  if (!isDownloadDisabledPreviewCandidate(file)) {
    return { compatible: false, reason: "Unsupported browser preview format, size, or delivery mode" };
  }
  if (inspection.error) return { compatible: false, reason: `Inspection failed — ${inspection.error}` };
  if (inspection.pending) return { compatible: false, reason: "Pending — inspecting actual encoding" };
  if (!result) return { compatible: false, reason: "Unverified — no inspection result was returned for this file" };
  if (result.fileName !== (file.fileName || file.name)) {
    return { compatible: false, reason: "Unverified — the source file changed; select it again" };
  }
  const compatible = result.compatible === true && result.unverified === false &&
    isDownloadDisabledPreviewCompatible({ ...file, mediaPreviewVerification: result.mediaPreviewVerification });
  if (compatible) return { compatible: true, reason: "Actual encoding verified — browser compatible" };
  return {
    compatible: false,
    reason: `${result.unverified || result.mediaPreviewVerification?.status === "unverified" ? "Unverified" : "Unsupported"} — ${result.reason || "Actual encoding has not been verified as browser compatible"}`,
  };
}

function MediaInspectionStatus({ inspection, testId }: {
  inspection: ReturnType<typeof useDownloadPolicyPreflight>;
  testId: string;
}) {
  return (
    <div
      className="rounded border border-gray-700 bg-gray-800/40 p-3 space-y-2 text-xs text-gray-300"
      data-testid={testId}
      data-preflight-pending={inspection.pending}
    >
      {inspection.pending
        ? <p className="flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Inspecting actual encoding… Saving stays blocked until verification completes.</p>
        : inspection.error
          ? <p className="text-amber-300">Inspection failed: {inspection.error}. Unverified files cannot be shared with downloads disabled.</p>
          : <p>Inspection complete. Only verified browser-compatible files can be shared with downloads disabled.</p>}
      <Button type="button" size="sm" variant="outline" className="h-7 border-gray-600"
        disabled={inspection.pending} onClick={inspection.retry} data-testid={`${testId}-retry`}>
        Retry inspection
      </Button>
    </div>
  );
}

function isPlayableThumbnailVideo(file: { fileName: string; mimeType?: string | null }): boolean {
  if (isDownloadOnlyFormat(file.fileName)) return false;
  const ext = file.fileName.split(".").pop()?.toLowerCase();
  return ext === "mp4" || ext === "webm" || ext === "m4v" ||
    file.mimeType?.startsWith("video/") === true;
}

function ProtectedFileThumb({ shareId, file }: {
  shareId: number;
  file: { id: number; fileName: string; hasThumb?: boolean; thumbStatus?: 'pending' | 'completed' | 'failed' | null; hasPreview?: boolean; mimeType?: string | null };
}) {
  const previousStatus = useRef(file.thumbStatus);
  const generation = useRef(0);
  if (previousStatus.current === "pending" && file.thumbStatus === "completed") {
    generation.current += 1;
  }
  previousStatus.current = file.thumbStatus;
  const source = `/api/preview-shares/${shareId}/files/${file.id}/thumb?v=${generation.current}`;
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const playableVideo = isPlayableThumbnailVideo(file);
  const image = file.hasPreview ||
    /\.(?:jpe?g|png|gif|webp|avif)$/i.test(file.fileName) ||
    file.mimeType?.startsWith("image/") === true;
  if (
    !file.hasThumb ||
    file.thumbStatus === "pending" ||
    file.thumbStatus === "failed" ||
    (!image && !playableVideo) ||
    failedSource === source
  ) {
    return (
      <span className="h-8 w-8 rounded bg-gray-800 border border-gray-700 shrink-0 flex items-center justify-center">
        {playableVideo
          ? <span className="relative" aria-label="Video"><FileIcon className="h-4 w-4 text-gray-400" /><span className="absolute inset-0 flex items-center justify-center text-[8px] text-white">▶</span></span>
          : <FileIcon className="h-4 w-4 text-gray-500" />}
      </span>
    );
  }
  return (
    <span className="relative h-8 w-8 rounded overflow-hidden bg-gray-800 border border-gray-700 shrink-0">
      <img
        src={source}
        alt=""
        loading="lazy"
        draggable={false}
        className="h-full w-full object-cover"
        onError={() => setFailedSource(source)}
      />
      {playableVideo && (
        <span className="absolute right-0.5 bottom-0.5 h-3.5 w-3.5 rounded-full bg-black/70 text-white flex items-center justify-center text-[7px]" aria-label="Video">▶</span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Activity protocol (Aktivitätsprotokoll) — chronological feed of everything
// that happened on the client page: visits, chat (inlined), downloads,
// white-label links, share lifecycle. Data survives page/file cleanup.
// ---------------------------------------------------------------------------

interface ActivityEvent {
  id: string;
  type: string;
  at: string;
  actorType: "client" | "team";
  actorName: string | null;
  text?: string;
  metadata?: {
    fileId?: number;
    fileName?: string;
    fileTitle?: string;
    dropboxPath?: string;
    files?: Array<{ fileId: number; fileName: string; dropboxPath?: string }>;
    childShareId?: number;
    childUrlPath?: string | null;
    childRevoked?: boolean;
    shareTitle?: string;
    expiresAt?: string;
    viaChildShare?: boolean;
    attachments?: Array<{ id: string; fileName: string; contentType: string; isMissing?: boolean }>;
  };
}

function formatActivityTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString("de-DE")} ${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
}

const ACTIVITY_ICONS: Record<string, { icon: typeof Eye; className: string }> = {
  client_visit: { icon: Eye, className: "text-[#6f88bf]" },
  chat_message: { icon: MessageCircle, className: "text-sky-400" },
  file_download: { icon: Download, className: "text-emerald-400" },
  zip_download: { icon: FileArchive, className: "text-emerald-400" },
  whitelabel_created: { icon: Share2, className: "text-violet-400" },
  share_created: { icon: Plus, className: "text-gray-400" },
  share_revoked: { icon: Ban, className: "text-red-400" },
};

function ActivityProtocol({ pulseId, shareId, onViewFile }: {
  pulseId: number;
  shareId: number;
  onViewFile: (shareId: number, fileId: number, fileName: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading } = useQuery<{ events: ActivityEvent[] }>({
    // Nested under the dialog's by-pulse key so prefix invalidations
    // (create/revoke/whitelabel) refresh the protocol too.
    queryKey: ["/api/preview-shares/by-pulse", pulseId, "activity"],
    queryFn: () => fetchJson(`/api/preview-shares/by-pulse/${pulseId}/activity`),
    staleTime: 30_000,
  });

  const events = data?.events ?? [];
  const shown = expanded ? events : events.slice(-30);

  const actorLabel = (e: ActivityEvent) =>
    e.actorName
      ? e.actorName + (e.actorType === "client" ? " (Kunde)" : "")
      : e.actorType === "client" ? "Kunde" : "Team";

  const describe = (e: ActivityEvent): React.ReactNode => {
    switch (e.type) {
      case "client_visit":
        return <>hat die Kundenseite geöffnet{e.metadata?.viaChildShare ? " (über Weitergabe-Link)" : ""}</>;
      case "chat_message":
        return (
          <>
            <span className="text-gray-500">schrieb:</span>{" "}
            <span className="text-gray-200 whitespace-pre-wrap break-words">{e.text}</span>
            {(e.metadata?.attachments?.length ?? 0) > 0 && (
              <span className="mt-1 flex flex-wrap gap-1.5">
                {e.metadata!.attachments!.map((att) =>
                  att.isMissing ? (
                    <span key={att.id} className="text-[10px] text-gray-600 italic">
                      {att.fileName} (nicht mehr verfügbar)
                    </span>
                  ) : att.contentType.startsWith("image/") ? (
                    // Cookie auth: the attachment route accepts the httpOnly
                    // access-token cookie, so a plain <img> works for the PM.
                    <a
                      key={att.id}
                      href={`/api/preview-shares/by-pulse/${pulseId}/activity/attachments/${att.id}`}
                      target="_blank"
                      rel="noreferrer"
                      title={att.fileName}
                    >
                      <img
                        src={`/api/preview-shares/by-pulse/${pulseId}/activity/attachments/${att.id}`}
                        alt={att.fileName}
                        loading="lazy"
                        className="max-h-24 max-w-[160px] rounded border border-gray-700 object-contain"
                      />
                    </a>
                  ) : (
                    <a
                      key={att.id}
                      href={`/api/preview-shares/by-pulse/${pulseId}/activity/attachments/${att.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] text-sky-300 hover:underline inline-flex items-center gap-1"
                    >
                      <Paperclip className="h-3 w-3" /> {att.fileName}
                    </a>
                  ),
                )}
              </span>
            )}
          </>
        );
      case "file_download":
        return (
          <>
            hat{" "}
            <button
              type="button"
              className="text-emerald-300 hover:underline font-medium"
              onClick={() => e.metadata?.fileId && onViewFile(shareId, e.metadata.fileId, e.metadata.fileName || "Datei")}
              title={e.metadata?.dropboxPath ? `Dropbox: ${e.metadata.dropboxPath}` : undefined}
              data-testid={`activity-file-${e.id}`}
            >
              {e.metadata?.fileTitle || e.metadata?.fileName || "eine Datei"}
            </button>{" "}
            heruntergeladen
            {e.metadata?.dropboxPath && (
              <span className="block text-[10px] text-gray-600 font-mono truncate" title={e.metadata.dropboxPath}>
                {e.metadata.dropboxPath}
              </span>
            )}
          </>
        );
      case "zip_download":
        return (
          <>
            hat alle Dateien als ZIP heruntergeladen ({e.metadata?.files?.length ?? 0} Dateien)
            {(e.metadata?.files?.length ?? 0) > 0 && (
              <span className="block text-[10px] text-gray-600 truncate">
                {e.metadata!.files!.map(f => f.fileName).join(", ")}
              </span>
            )}
          </>
        );
      case "whitelabel_created":
        return (
          <>
            hat einen Weitergabe-Link erstellt
            {e.metadata?.childUrlPath ? (
              <>
                {": "}
                <a
                  href={e.metadata.childUrlPath}
                  target="_blank"
                  rel="noreferrer"
                  className={`hover:underline ${e.metadata.childRevoked ? "text-gray-500 line-through" : "text-violet-300"}`}
                >
                  {window.location.origin}{e.metadata.childUrlPath}
                </a>
                {e.metadata.childRevoked && <span className="text-red-400"> (widerrufen)</span>}
              </>
            ) : null}
          </>
        );
      case "share_created":
        return <>hat den Link erstellt{e.metadata?.shareTitle ? `: „${e.metadata.shareTitle}"` : ""}</>;
      case "share_revoked":
        return <>hat den Link widerrufen{e.metadata?.viaChildShare ? " (Weitergabe-Link)" : ""}</>;
      default:
        return <>{e.type}</>;
    }
  };

  return (
    <div className="border border-gray-700 rounded" data-testid="activity-protocol">
      <div className="px-3 py-1.5 text-xs text-gray-400 border-b border-gray-800 flex items-center gap-1.5">
        <History className="h-3.5 w-3.5" /> Aktivitätsprotokoll{events.length > 0 && ` (${events.length})`}
      </div>
      {isLoading ? (
        <div className="p-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-gray-500" /></div>
      ) : events.length === 0 ? (
        <div className="p-4 text-center text-xs text-gray-500">Noch keine Aktivität</div>
      ) : (
        <div className="max-h-[30vh] overflow-y-auto">
          {events.length > shown.length && (
            <button
              type="button"
              className="w-full py-1.5 text-[11px] text-gray-500 hover:text-gray-300"
              onClick={() => setExpanded(true)}
            >
              {events.length - shown.length} ältere Einträge anzeigen
            </button>
          )}
          <ol className="px-3 py-2 space-y-0">
            {shown.map((e, i) => {
              const cfg = ACTIVITY_ICONS[e.type] ?? { icon: Info, className: "text-gray-500" };
              const Icon = cfg.icon;
              const isTeam = e.actorType === "team";
              return (
                <li
                  key={e.id}
                  className={`relative flex gap-2.5 pb-2.5 last:pb-0 ${isTeam ? "flex-row-reverse" : ""}`}
                >
                  {/* timeline rail (client side left, team side right) */}
                  {i < shown.length - 1 && (
                    <span
                      className={`absolute top-4 bottom-0 w-px bg-gray-800 ${isTeam ? "right-[7px]" : "left-[7px]"}`}
                      aria-hidden
                    />
                  )}
                  <span className={`mt-0.5 shrink-0 ${cfg.className}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className={`min-w-0 flex-1 text-xs text-gray-300 ${isTeam ? "text-right" : ""}`}>
                    <span className={`font-medium ${isTeam ? "text-gray-400" : "text-gray-200"}`}>
                      {actorLabel(e)}
                    </span>{" "}
                    {describe(e)}
                    <span className="block text-[10px] text-gray-600 mt-0.5">{formatActivityTime(e.at)}</span>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}

function formatBytes(bytes?: number): string {
  if (!bytes) return "";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

const BADGE_CLASSES: Record<OriginLabel, string> = {
  final: "bg-emerald-900/60 text-emerald-300",
  abnahme: "bg-amber-900/60 text-amber-300",
  preview: "bg-indigo-900/60 text-indigo-300",
  other: "bg-gray-700/60 text-gray-300",
};

function OriginBadge({ label }: { label: OriginLabel }) {
  return (
    <span className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded ${BADGE_CLASSES[label]}`}>
      {ORIGIN_LABEL_DISPLAY[label]}
    </span>
  );
}

/** Color-coded file-format chip (ProRes / MP4 / PDF / …), shared palette
 *  with the client page so PM and client see the same badge. */
function FileFormatBadge({ fileName }: { fileName: string }) {
  const fmt = detectFileFormat(fileName);
  return (
    <span
      className="text-[9px] font-extrabold tracking-wider px-1.5 py-0.5 rounded border shrink-0"
      style={{ color: fmt.color, borderColor: `${fmt.color}59`, backgroundColor: `${fmt.color}29` }}
    >
      {fmt.label}
    </span>
  );
}

/** Hues for format groups — same palette as the client page. */
const GROUP_COLORS = ["#7dd3fc", "#f0abfc", "#86efac", "#fcd34d", "#fda4af"];

/** Map each grouped file id → its group accent color (adjacent runs). */
function computeGroupColors(files: Array<{ id: number; groupKey: number | null }>): Map<number, string> {
  const colors = new Map<number, string>();
  let n = 0;
  for (let i = 0; i < files.length; i++) {
    const g = files[i].groupKey;
    if (g == null) continue;
    if (i > 0 && files[i - 1].groupKey === g) {
      colors.set(files[i].id, colors.get(files[i - 1].id)!);
    } else if (files[i + 1]?.groupKey === g) {
      colors.set(files[i].id, GROUP_COLORS[n++ % GROUP_COLORS.length]);
    }
  }
  return colors;
}

type DropHint = { id: number; pos: "before" | "after" | "group" };

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await authedFetch(url, init);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as any)?.error || `Request failed (${res.status})`);
  return body as T;
}

/** Dropbox file picker with multi-select, auto-navigation + shortcuts. */
function SharedFilePicker({
  projectId,
  selected,
  onToggle,
}: {
  projectId: string;
  selected: Map<string, DropboxEntry>;
  onToggle: (entry: DropboxEntry) => void;
}) {
  const [currentPath, setCurrentPath] = useState<string>("");
  const [entries, setEntries] = useState<DropboxEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Auto-navigate to the derived project folder (graceful fallback).
  const { data: folderInfo, isLoading: folderLoading, isError: folderIsError, error: folderError } = useQuery<{
    path: string; notes: string[]; shortcuts: { name: string; path: string }[];
  }>({
    queryKey: ["/api/preview-shares/project-folder", projectId],
    queryFn: () => fetchJson(`/api/preview-shares/project-folder/${encodeURIComponent(projectId)}`),
  });

  useEffect(() => {
    if (folderInfo?.path) setCurrentPath(folderInfo.path);
  }, [folderInfo?.path]);

  const { data: listData, isLoading: listLoading, isError: listIsError, error: listError, refetch: refetchList, isRefetching } = useQuery<{
    entries: DropboxEntry[]; cursor?: string | null; hasMore?: boolean;
  }>({
    queryKey: ["/api/dropbox-readonly/list", currentPath],
    enabled: !!currentPath,
    staleTime: 0,
    queryFn: () => fetchJson(`/api/dropbox-readonly/list?path=${encodeURIComponent(currentPath)}`),
  });

  useEffect(() => {
    if (listData) {
      setEntries(listData.entries || []);
      setCursor(listData.cursor || null);
      setHasMore(!!listData.hasMore);
    }
  }, [listData]);

  // Live updates without hammering the API: Dropbox longpoll holds ONE
  // request open (~30 s) and answers only when the folder changed —
  // exactly the "just rendered, not yet synced" case. On change we
  // re-fetch the listing once. Errors back off and eventually give up
  // (the manual refresh button still works).
  useEffect(() => {
    if (!currentPath) return;
    let stopped = false;
    let errors = 0;
    const aborter = new AbortController();

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    (async () => {
      while (!stopped && errors < 5) {
        try {
          const { cursor: watchCursor } = await fetchJson<{ cursor: string }>(
            `/api/dropbox-readonly/latest-cursor?path=${encodeURIComponent(currentPath)}`,
            { signal: aborter.signal },
          );
          let pollCursor = watchCursor;
          while (!stopped) {
            const result = await fetchJson<{ changes: boolean; backoff?: number; reset?: boolean }>(
              `/api/dropbox-readonly/longpoll?cursor=${encodeURIComponent(pollCursor)}`,
              { signal: aborter.signal },
            );
            if (stopped) return;
            errors = 0;
            if (result.reset) break; // fetch a fresh cursor
            if (result.changes) {
              await refetchList();
              break; // re-anchor the cursor to the fresh state
            }
            if (result.backoff) await sleep(result.backoff * 1000);
          }
        } catch (err: any) {
          if (stopped || err?.name === "AbortError") return;
          errors++;
          await sleep(5000 * errors);
        }
      }
    })();

    return () => {
      stopped = true;
      // Also cancels the hanging longpoll request so the server frees
      // its watcher slot immediately when the dialog closes.
      aborter.abort();
    };
  }, [currentPath, refetchList]);

  const navigateTo = useCallback((path: string) => {
    setCurrentPath(path);
    setEntries([]);
    setCursor(null);
    setHasMore(false);
  }, []);

  const handleLoadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const data = await fetchJson<{ entries: DropboxEntry[]; cursor?: string | null; hasMore?: boolean }>(
        `/api/dropbox-readonly/list?path=${encodeURIComponent(currentPath)}&cursor=${encodeURIComponent(cursor)}`,
      );
      setEntries((prev) => [...prev, ...(data.entries || [])]);
      setCursor(data.cursor || null);
      setHasMore(!!data.hasMore);
    } finally {
      setLoadingMore(false);
    }
  };

  // Breadcrumbs from the auto-navigated base down.
  const crumbs = useMemo(() => {
    const base = folderInfo?.path || "";
    if (!base || !currentPath.startsWith(base)) return [];
    const baseName = base.split("/").filter(Boolean).pop() || base;
    const list = [{ name: baseName, path: base }];
    const rest = currentPath.slice(base.length).split("/").filter(Boolean);
    let accum = base;
    for (const part of rest) {
      accum = `${accum}/${part}`;
      list.push({ name: part, path: accum });
    }
    return list;
  }, [folderInfo?.path, currentPath]);

  if (folderLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Locating project folder…
      </div>
    );
  }
  if (folderIsError) {
    return (
      <Alert className="bg-red-950/40 border-red-800">
        <AlertTriangle className="h-4 w-4 text-red-400" />
        <AlertDescription className="text-red-300">
          {(folderError as Error)?.message || "Could not open the Dropbox folder"}
        </AlertDescription>
      </Alert>
    );
  }

  const folders = entries.filter((e) => e.type === "folder");
  const files = entries.filter((e) => e.type === "file");

  return (
    <div className="space-y-2">
      {(folderInfo?.notes?.length ?? 0) > 0 && (
        <Alert className="bg-amber-950/30 border-amber-800 py-2">
          <AlertDescription className="text-amber-300 text-xs">
            {folderInfo!.notes.join(" ")}
          </AlertDescription>
        </Alert>
      )}

      {/* Delivery-folder shortcuts */}
      {(folderInfo?.shortcuts?.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {folderInfo!.shortcuts.map((s) => (
            <Button
              key={s.path}
              size="sm"
              variant="outline"
              className="h-6 px-2 text-xs border-gray-600 text-gray-300 hover:text-white"
              onClick={() => navigateTo(s.path)}
              data-testid={`shortcut-${s.name}`}
            >
              <OriginBadge label={classifyFolderName(s.name)} />
              <span className="ml-1.5">{s.name}</span>
            </Button>
          ))}
        </div>
      )}

      {/* Breadcrumbs + refresh */}
      <div className="flex items-center gap-1 text-xs text-gray-400">
        <div className="flex items-center flex-wrap flex-1 min-w-0">
          {crumbs.map((c, i) => (
            <span key={c.path} className="flex items-center">
              {i > 0 && <ChevronRight className="h-3 w-3 mx-0.5" />}
              <button
                className={`hover:text-white ${i === crumbs.length - 1 ? "text-white" : ""}`}
                onClick={() => navigateTo(c.path)}
              >
                {c.name}
              </button>
            </span>
          ))}
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs text-gray-400 hover:text-white shrink-0"
          onClick={() => refetchList()}
          disabled={isRefetching || listLoading}
          title="Refresh file list"
          data-testid="button-refresh-files"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? "animate-spin" : ""}`} />
          <span className="ml-1">Refresh</span>
        </Button>
      </div>

      {/* Entries */}
      <div className="border border-gray-700 rounded max-h-[48vh] overflow-y-auto divide-y divide-gray-800">
        {listLoading && (
          <div className="flex items-center justify-center py-6 text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        )}
        {listIsError && (
          <div className="p-3 text-xs text-red-300">{(listError as Error)?.message}</div>
        )}
        {!listLoading && entries.length === 0 && !listIsError && (
          <div className="p-4 text-center text-xs text-gray-500">This folder is empty</div>
        )}
        {folders.map((f) => (
          <button
            key={f.path}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-800/60 text-left"
            onClick={() => navigateTo(f.path)}
            data-testid={`folder-${f.name}`}
          >
            <Folder className="h-4 w-4 text-blue-400 shrink-0" />
            <span className="truncate flex-1">{f.name}</span>
            {isDeliveryFolder(f.name) && <OriginBadge label={classifyFolderName(f.name)} />}
            <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
          </button>
        ))}
        {files.map((f) => {
          const isSelected = selected.has(f.path);
          return (
            <label
              key={f.path}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer ${isSelected ? "bg-blue-950/40 text-white" : "text-gray-200 hover:bg-gray-800/60"}`}
              data-testid={`file-${f.name}`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggle(f)}
                className="accent-blue-500"
              />
              <FileIcon className="h-4 w-4 text-gray-400 shrink-0" />
              <span className="truncate flex-1">{f.name}</span>
              <OriginBadge label={classifyPathOrigin(f.path)} />
              <span className="text-xs text-gray-500 w-16 text-right">{formatBytes(f.size)}</span>
            </label>
          );
        })}
        {hasMore && (
          <div className="p-2 text-center">
            <Button size="sm" variant="ghost" onClick={handleLoadMore} disabled={loadingMore} className="text-xs text-gray-400">
              {loadingMore ? <Loader2 className="h-3 w-3 animate-spin" /> : "Load more"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function TransferStatusIcon({ file }: { file: ShareFileInfo }) {
  if (file.deliveryMode === "dropbox_direct") return <CheckCircle2 className="h-4 w-4 text-emerald-400" aria-label="Direct link" />;
  switch (file.transferStatus) {
    case "completed":
      // Transfer done, but a derived preview (e.g. PSD → PNG) is still
      // being generated — keep spinning so "all done" really means done.
      if (file.previewStatus === "pending") {
        return <Loader2 className="h-4 w-4 text-sky-400 animate-spin" aria-label="Generating preview" />;
      }
      return <CheckCircle2 className="h-4 w-4 text-emerald-400" aria-label="Ready" />;
    case "failed": return <XCircle className="h-4 w-4 text-red-400" aria-label="Failed" />;
    case "transferring": return <Loader2 className="h-4 w-4 text-blue-400 animate-spin" aria-label="Copying" />;
    default: return <Clock className="h-4 w-4 text-gray-500" aria-label="Queued" />;
  }
}

/** Earliest selectable expiry: tomorrow (local time) as yyyy-mm-dd. */
function minExpiryDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Calendar days from today until the given yyyy-mm-dd date. */
function daysUntil(dateStr: string): number {
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOfDay(new Date(`${dateStr}T12:00:00`)) - startOfDay(new Date())) / 86_400_000);
}

/** " (in 50 days)" suffix for expiry labels; empty for invalid/past input. */
function inDaysSuffix(dateStr: string | undefined | null): string {
  if (!dateStr) return "";
  const days = daysUntil(dateStr.slice(0, 10));
  if (!Number.isFinite(days) || days < 1) return "";
  return days === 1 ? " (in 1 day)" : ` (in ${days} days)`;
}

function defaultCreateExpiry(shareType: 'preview' | 'final'): string {
  const days = shareType === 'final' ? 730 : 60;
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

export function PreviewShareDialog({ open, onOpenChange, projectId, pulseId, requestedShareType, senderName = 'Acolori Team' }: PreviewShareDialogProps) {
  useEffect(() => {
    if (open && pulseId) void import('@/lib/pulse-user-tags').then(m => m.recordPulseRead(pulseId));
  }, [open, pulseId]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Task #189: resolve the effective share type (create = requestedShareType ?? 'preview'; manage = from share data)
  const effectiveCreateType: 'preview' | 'final' = requestedShareType ?? 'preview';
  const isFinalCreate = effectiveCreateType === 'final';

  const [selected, setSelected] = useState<Map<string, DropboxEntry>>(new Map());
  const [title, setTitle] = useState(isFinalCreate ? "Final" : "Preview");
  const [password, setPassword] = useState("");
  const [expiresAt, setExpiresAt] = useState<string>(() => defaultCreateExpiry(effectiveCreateType));
  const [createdShare, setCreatedShare] = useState<ShareInfo | null>(null);
  const [sendLinkOpen, setSendLinkOpen] = useState(false);
  const [addingFiles, setAddingFiles] = useState(false);
  const [recipientContactId, setRecipientContactId] = useState<string>("");
  // Optional page sections (checkboxes): all default OFF.
  const [showChat, setShowChat] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const [showDates, setShowDates] = useState(false);
  const [showClientProjects, setShowClientProjects] = useState(false);
  // White-label re-sharing (client branding) — PM opt-in, default OFF.
  const [allowClientBranding, setAllowClientBranding] = useState(false);
  const [disableDownloads, setDisableDownloads] = useState(false);
  const [enableClientUploads, setEnableClientUploads] = useState(false);
  // An incompatible manage toggle is a request, NOT a saved policy or a
  // request to delete delivered files. The PM removes those explicitly.
  const [manageDownloadRestrictionRequested, setManageDownloadRestrictionRequested] = useState(false);
  // Client-facing per-file title/description (task #73), keyed by Dropbox path.
  const [fileMeta, setFileMeta] = useState<Map<string, { title: string; description: string }>>(new Map());
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<{ title: string; description: string }>({ title: "", description: "" });
  // Drag & drop ordering/grouping of shared files (manage mode).
  const [dragFileId, setDragFileId] = useState<number | null>(null);
  const [dropHint, setDropHint] = useState<DropHint | null>(null);
  const thumbnailPollingStartedAt = useRef<number | null>(null);

  // Task #189: Final replacement flow state.
  /** Whether the PM confirmed they want to proceed replacing the existing Final. */
  const [finalReplaceConfirmed, setFinalReplaceConfirmed] = useState(false);
  /** Old Final file IDs the PM selected to copy independently into the new Final. */
  const [carryOverFileIds, setCarryOverFileIds] = useState<Set<number>>(new Set());
  /** Resulting Preview expiry when replacing a Final (default +60d). */
  const [oldFinalPreviewExpiry, setOldFinalPreviewExpiry] = useState<string>(() => {
    const d = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 10);
  });

  // Reset per open.
  useEffect(() => {
    if (open) {
      thumbnailPollingStartedAt.current = null;
      // The host may reuse this mounted dialog when switching entry points.
      // Always reset type-dependent defaults so Preview state can never leak
      // into a Final creation (or vice versa).
      setTitle(isFinalCreate ? "Final" : "Preview");
      setExpiresAt(defaultCreateExpiry(effectiveCreateType));
      setSelected(new Map());
      setCreatedShare(null);
      setSendLinkOpen(false);
      setAddingFiles(false);
      setPassword("");
      setRecipientContactId("");
      setShowChat(false);
      setShowTeam(false);
      setShowDates(false);
      setShowClientProjects(false);
      setAllowClientBranding(false);
      setDisableDownloads(false);
      setEnableClientUploads(false);
      setManageDownloadRestrictionRequested(false);
      setFileMeta(new Map());
      setEditingFileId(null);
      setFinalReplaceConfirmed(false);
      setCarryOverFileIds(new Set());
      const d60 = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
      setOldFinalPreviewExpiry(d60.toISOString().slice(0, 10));
    } else {
      thumbnailPollingStartedAt.current = null;
      // Closing must discard any pending drag/group decision — otherwise a
      // stale modal could resurrect and save an obsolete order later.
      setPendingGroup(null);
      setPendingGroupTitle("");
      setDragFileId(null);
      setDropHint(null);
    }
  }, [open, effectiveCreateType, isFinalCreate]);

  const manageMode = !!pulseId || !!createdShare;
  const effectivePulseId = pulseId ?? createdShare?.pulseId;

  const { data: fetchedShare, isLoading: shareLoading, isError: shareIsError } = useQuery<ShareInfo>({
    queryKey: ["/api/preview-shares/by-pulse", effectivePulseId],
    enabled: open && !!effectivePulseId,
    queryFn: () => fetchJson(`/api/preview-shares/by-pulse/${effectivePulseId}`),
    refetchInterval: (query) => {
      const s = query.state.data;
      const transferring = s?.files.some(f =>
        f.transferStatus === "pending" ||
        f.transferStatus === "transferring" ||
        (f.transferStatus === "completed" && f.previewStatus === "pending")
      );
      if (transferring) return 5000;
      const thumbnailPending = s?.files.some(f =>
        f.transferStatus === "completed" && f.thumbStatus === "pending"
      );
      if (!thumbnailPending) {
        thumbnailPollingStartedAt.current = null;
        return false;
      }
      thumbnailPollingStartedAt.current ??= Date.now();
      if (Date.now() - thumbnailPollingStartedAt.current >= 50 * 60 * 1000) return false;
      // React Query pauses refetch intervals while the document is hidden
      // (refetchIntervalInBackground defaults to false) and resumes on focus.
      return 15_000;
    },
  });

  // Creation starts with the POST response, whose files may still be pending.
  // Keep that local share synchronized with the canonical by-pulse response so
  // readiness-dependent actions appear without requiring an unrelated edit.
  useEffect(() => {
    if (createdShare && fetchedShare?.id === createdShare.id) {
      setCreatedShare(fetchedShare);
    }
  }, [createdShare?.id, fetchedShare]);

  const share = createdShare ?? fetchedShare ?? null;
  const effectiveProjectId = projectId || share?.projectId;
  const { data: projectDetails } = useQuery<{ projectName?: string }>({
    queryKey: ["/api/projects", effectiveProjectId],
    enabled: open && !!effectiveProjectId,
    queryFn: () => fetchJson(`/api/projects/${encodeURIComponent(effectiveProjectId!)}`),
  });
  const effectiveProjectName = projectDetails?.projectName?.trim() || "";

  // Task #78: verify dropbox_direct files still exist at their stored
  // paths (rename/delete in Dropbox breaks the client's direct link).
  // One cheap on-open check + manual re-check; no constant polling.
  const hasDirectFiles = !!share?.files.some((f) => f.deliveryMode === "dropbox_direct");
  const { data: verifyData, isFetching: verifying, refetch: recheckFiles } = useQuery<{
    files: Array<{ id: number; status: "ok" | "missing" | "unknown" }>;
    checkedAt: string;
  }>({
    queryKey: ["/api/preview-shares/verify-files", share?.id],
    enabled: open && manageMode && !!share && !share.revokedAt && hasDirectFiles,
    staleTime: 60_000,
    queryFn: () => fetchJson(`/api/preview-shares/${share!.id}/verify-files`),
  });
  const missingFileIds = useMemo(
    () => new Set((verifyData?.files || []).filter((f) => f.status === "missing").map((f) => f.id)),
    [verifyData],
  );
  const unknownFileCount = useMemo(
    () => (verifyData?.files || []).filter((f) => f.status === "unknown").length,
    [verifyData],
  );

  // Recipient contact (task #72): the client page greets this person by
  // first name. Options are the project client's contact persons.
  const clientId = clientIdFromProjectId(effectiveProjectId);
  const { data: contactOptions = [] } = useQuery<ContactPersonOption[]>({
    queryKey: ["/api/contact-persons", { clientId }],
    enabled: open && !!clientId,
    queryFn: () => fetchJson(`/api/contact-persons?clientId=${encodeURIComponent(clientId!)}`),
  });

  const invalidateShare = () => {
    if (pulseId) queryClient.invalidateQueries({ queryKey: ["/api/preview-shares/by-pulse", pulseId] });
  };

  // Task #189: load the active Final share for this project (create-Final mode only).
  const { data: activeFinalShare, isLoading: activeFinalLoading } = useQuery<ActiveFinalShareSummary | null>({
    queryKey: ["/api/preview-shares/active-final", projectId],
    enabled: open && !manageMode && isFinalCreate && !!projectId,
    queryFn: async () => {
      const res = await authedFetch(`/api/preview-shares/active-final/${encodeURIComponent(projectId!)}`);
      if (res.status === 404) return null;
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((body as any)?.error || `Request failed (${res.status})`);
      return ((body as any)?.activeFinal ?? null) as ActiveFinalShareSummary | null;
    },
    staleTime: 30_000,
    retry: false,
  });

  const carryOverSelected = isFinalCreate && activeFinalShare && finalReplaceConfirmed
    ? activeFinalShare.files.filter(f => carryOverFileIds.has(f.id)) : [];
  const prospectiveInspection = useDownloadPolicyPreflight(
    open && ((!manageMode && disableDownloads) || (manageMode && addingFiles && share?.disableDownloads === true)),
    {
      projectId: effectiveProjectId ?? "",
      // Prospective Final copies must inspect original Dropbox paths, never
      // fileIds or derivative/verification metadata from the old share.
      files: Array.from(new Set([
        ...Array.from(selected.keys()), ...carryOverSelected.map(f => f.dropboxPath),
      ])).sort().map(path => ({ path })),
    },
    JSON.stringify([
      Array.from(selected.values()).map(f => [f.path, f.name, f.size]),
      carryOverSelected.map(f => [f.id, f.dropboxPath, f.fileName, f.sizeBytes]),
    ]),
  );
  const deliveredInspection = useDownloadPolicyPreflight(
    open && manageMode && manageDownloadRestrictionRequested && !!share,
    {
      projectId: effectiveProjectId ?? "",
      shareId: share?.id,
      fileIds: share?.files.map(f => f.id).sort((a, b) => a - b) ?? [],
    },
    JSON.stringify(share?.files.map(f => [
      f.id, f.fileName, f.sizeBytes, f.transferStatus, f.previewStatus, f.hasPreview,
      f.deliveryMode, f.mediaPreviewVerification,
    ])),
  );
  const selectedOutcome = (f: DropboxEntry) => inspectionOutcome(
    { fileName: f.name, size: f.size },
    prospectiveInspection.files.find(result => result.path?.toLowerCase() === f.path.toLowerCase()),
    prospectiveInspection,
  );
  const carryOverOutcome = (f: ActiveFinalShareSummary["files"][number]) => inspectionOutcome(
    // The old file's PSD derivative is not available to a new transfer.
    { fileName: f.fileName, sizeBytes: f.sizeBytes },
    prospectiveInspection.files.find(result => result.path?.toLowerCase() === f.dropboxPath.toLowerCase()),
    prospectiveInspection,
  );
  const deliveredOutcome = (f: ShareFileInfo) => manageDownloadRestrictionRequested
    ? inspectionOutcome(f, deliveredInspection.files.find(result => result.fileId === f.id), deliveredInspection)
    : { compatible: isDownloadDisabledPreviewCompatible(f), reason: f.mediaPreviewVerification?.reason || "Browser preview or actual encoding is not verified" };
  const incompatibleSelected = Array.from(selected.values()).filter(f => !selectedOutcome(f).compatible);
  const incompatibleCarryOver = carryOverSelected.filter(f => !carryOverOutcome(f).compatible);
  const incompatibleDelivered = share?.files.filter(f => !deliveredOutcome(f).compatible) ?? [];
  const creationBlockedByDownloads = disableDownloads &&
    (incompatibleSelected.length > 0 || incompatibleCarryOver.length > 0);
  const addingBlockedByDownloads = share?.disableDownloads === true && incompatibleSelected.length > 0;
  const previewLimitGiB = SHARE_BROWSER_PREVIEW_MAX_BYTES / 1024 ** 3;

  const removeAllIncompatibleSelections = () => {
    // Only prospective files/copies are deselected. The old Final and any
    // already-delivered files remain completely untouched.
    const paths = new Set(incompatibleSelected.map(f => f.path));
    setSelected(prev => new Map(Array.from(prev).filter(([path]) => !paths.has(path))));
    setFileMeta(prev => new Map(Array.from(prev).filter(([path]) => !paths.has(path))));
    const ids = new Set(incompatibleCarryOver.map(f => f.id));
    setCarryOverFileIds(prev => new Set(Array.from(prev).filter(id => !ids.has(id))));
  };

  const assertCompatibleCreation = () => {
    if (creationBlockedByDownloads) {
      throw new Error("Disable downloads requires every selected file to support browser preview. Remove all non-viewable files first.");
    }
  };

  // Task #189: replaceFinal mutation.
  const replaceFinalMutation = useMutation({
    mutationFn: async () => {
      assertCompatibleCreation();
      const res = await authedFetch("/api/preview-shares", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          title,
          shareType: 'final',
          password: password || undefined,
          expiresAt: new Date(`${expiresAt}T23:59:59`).toISOString(),
          recipientContactId: recipientContactId || undefined,
          showChat,
          showTeam,
          showDates,
          showClientProjects,
          allowClientBranding,
          disableDownloads,
          enableClientUploads: false,
          files: selectedFilesPayload(),
          replaceFinal: true,
          oldFinalShareId: activeFinalShare!.id,
          carryOverFileIds: Array.from(carryOverFileIds),
          oldFinalExpiresAt: new Date(`${oldFinalPreviewExpiry}T23:59:59`).toISOString(),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409) {
        // Structured conflict: another active Final share exists.
        throw Object.assign(new Error((body as any)?.error || "Conflict: another Final share exists"), { status: 409, body });
      }
      if (!res.ok) throw new Error((body as any)?.error || `Request failed (${res.status})`);
      return body as ShareInfo;
    },
    onSuccess: (data) => {
      setCreatedShare(data);
      queryClient.invalidateQueries({ queryKey: ["/api/tree/snapshot"] });
      queryClient.invalidateQueries({ queryKey: ["/api/preview-shares/active-final", projectId] });
      toast({ title: "Final share created", description: "Previous Final converted to Preview. Files are being prepared — copy the link below." });
    },
    onError: (err: any) => {
      const is409 = (err as any)?.status === 409;
      const currentFinal = (err as any)?.body?.existingFinal as ActiveFinalShareSummary | undefined;
      if (currentFinal) {
        queryClient.setQueryData(
          ["/api/preview-shares/active-final", projectId],
          currentFinal,
        );
        setFinalReplaceConfirmed(false);
      } else if (is409) {
        queryClient.invalidateQueries({ queryKey: ["/api/preview-shares/active-final", projectId] });
      }
      toast({
        title: is409 ? "Final share conflict" : "Could not create Final share",
        description: err?.message,
        variant: "destructive",
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      assertCompatibleCreation();
      const body: Record<string, unknown> = {
        projectId,
        title,
        shareType: effectiveCreateType,
        password: password || undefined,
        expiresAt: new Date(`${expiresAt}T23:59:59`).toISOString(),
        recipientContactId: recipientContactId || undefined,
        showChat,
        showTeam,
        showDates,
        showClientProjects: isFinalCreate && showClientProjects,
        allowClientBranding,
        disableDownloads,
        enableClientUploads: !isFinalCreate && enableClientUploads,
        files: selectedFilesPayload(),
      };
      const res = await authedFetch("/api/preview-shares", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const resBody = await res.json().catch(() => ({}));
      if (res.status === 409) {
        throw Object.assign(new Error((resBody as any)?.error || "Conflict: a Final share already exists for this project"), { status: 409, body: resBody });
      }
      if (!res.ok) throw new Error((resBody as any)?.error || `Request failed (${res.status})`);
      return resBody as ShareInfo;
    },
    onSuccess: (data) => {
      setCreatedShare(data);
      queryClient.invalidateQueries({ queryKey: ["/api/tree/snapshot"] });
      toast({ title: "Share created", description: "Files are being prepared — copy the link below." });
    },
    onError: (err: any) => {
      const is409 = (err as any)?.status === 409;
      const currentFinal = (err as any)?.body?.existingFinal as ActiveFinalShareSummary | undefined;
      if (currentFinal) {
        queryClient.setQueryData(
          ["/api/preview-shares/active-final", projectId],
          currentFinal,
        );
      } else if (is409) {
        queryClient.invalidateQueries({ queryKey: ["/api/preview-shares/active-final", projectId] });
      }
      toast({
        title: is409 ? "Final share conflict" : "Could not create share",
        description: err?.message,
        variant: "destructive",
      });
    },
  });

  const patchMutation = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      if (patch.disableDownloads === true && incompatibleDelivered.length > 0) {
        throw new Error("Remove the incompatible delivered files explicitly before disabling downloads.");
      }
      return fetchJson<ShareInfo>(`/api/preview-shares/${share!.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
    },
    onSuccess: (data, patch) => {
      if (createdShare) setCreatedShare({ ...data, children: data.children ?? createdShare.children });
      if (effectivePulseId) queryClient.setQueryData<ShareInfo>(["/api/preview-shares/by-pulse", effectivePulseId],
        prev => ({ ...data, children: data.children ?? prev?.children }));
      if (typeof patch.disableDownloads === "boolean") setManageDownloadRestrictionRequested(false);
      invalidateShare();
      toast({ title: "Share updated" });
    },
    onError: (err: any) => toast({ title: "Update failed", description: err?.message, variant: "destructive" }),
  });

  const revokeMutation = useMutation({
    mutationFn: async () => fetchJson(`/api/preview-shares/${share!.id}/revoke`, { method: "POST" }),
    onSuccess: () => {
      invalidateShare();
      if (createdShare) setCreatedShare({ ...createdShare, revokedAt: new Date().toISOString() });
      toast({ title: "Share revoked", description: "The link no longer works; shared copies will be deleted." });
    },
    onError: (err: any) => toast({ title: "Revoke failed", description: err?.message, variant: "destructive" }),
  });

  const addFilesMutation = useMutation({
    mutationFn: async () => {
      if (addingBlockedByDownloads) throw new Error("Remove the non-viewable files before adding files to a share with downloads disabled.");
      return fetchJson<ShareInfo>(`/api/preview-shares/${share!.id}/files`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ files: selectedFilesPayload() }),
      });
    },
    onSuccess: (data) => {
      if (createdShare) setCreatedShare(data);
      invalidateShare();
      setSelected(new Map());
      setAddingFiles(false);
      toast({ title: "Files added" });
    },
    onError: (err: any) => toast({ title: "Could not add files", description: err?.message, variant: "destructive" }),
  });

  const removeFileMutation = useMutation({
    mutationFn: async (fileId: number) => fetchJson<ShareInfo>(`/api/preview-shares/${share!.id}/files/${fileId}`, { method: "DELETE" }),
    onSuccess: (data: any) => {
      if (createdShare && data?.id) setCreatedShare(data);
      invalidateShare();
    },
    onError: (err: any) => toast({ title: "Could not remove file", description: err?.message, variant: "destructive" }),
  });

  const retryMutation = useMutation({
    mutationFn: async (fileId: number) => fetchJson(`/api/preview-shares/${share!.id}/files/${fileId}/retry`, { method: "POST" }),
    onSuccess: () => invalidateShare(),
  });

  const reorderMutation = useMutation({
    mutationFn: async (order: Array<{ id: number; groupKey: number | null; groupKind?: string | null; groupTitle?: string | null }>) =>
      fetchJson<ShareInfo>(`/api/preview-shares/${share!.id}/files/order`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order }),
      }),
    onSuccess: (data) => {
      // serializeShare has no `children` — keep the existing child-link
      // list in place instead of letting it vanish until the next fetch.
      if (createdShare) setCreatedShare({ ...data, children: createdShare.children });
      if (pulseId) {
        queryClient.setQueryData<ShareInfo>(["/api/preview-shares/by-pulse", pulseId], (prev) =>
          prev ? { ...data, children: prev.children } : data);
      }
    },
    onError: (err: any) => toast({ title: "Could not save order", description: err?.message, variant: "destructive" }),
  });

  /** A grouping drop waiting for the PM's same-content/related decision. */
  const [pendingGroup, setPendingGroup] = useState<{
    order: Array<{ id: number; groupKey: number | null; groupKind: string | null; groupTitle: string | null }>;
    anchor: number;
    names: string[];
  } | null>(null);
  /** Optional custom group title typed in the grouping modal. */
  const [pendingGroupTitle, setPendingGroupTitle] = useState("");
  const pendingGroupTitleRef = useRef<HTMLInputElement>(null);

  const cancelPendingGroup = () => {
    setPendingGroup(null);
    setPendingGroupTitle("");
  };

  /** Drop `dragged` before/after `target`, or ONTO it to group them. */
  const applyDrop = (draggedId: number, targetId: number, pos: DropHint["pos"]) => {
    if (!share || draggedId === targetId) return;
    const files = share.files;
    const dragged = files.find((f) => f.id === draggedId);
    const target = files.find((f) => f.id === targetId);
    if (!dragged || !target) return;
    const rest = files.filter((f) => f.id !== draggedId);
    const ti = rest.findIndex((f) => f.id === targetId);
    const insertAt = pos === "before" ? ti : ti + 1;
    // Grouping: dragged joins the target's group (or forms a new one).
    // The target must be stamped too — otherwise the server sees a
    // singleton group on the dragged file and clears it (plain reorder).
    const groupAnchor = pos === "group" ? (target.groupKey ?? target.id) : null;
    const next = [...rest.slice(0, insertAt), { ...dragged, groupKey: groupAnchor ?? dragged.groupKey }, ...rest.slice(insertAt)];
    const order = next.map((f) => ({
      id: f.id,
      groupKey: (groupAnchor != null && f.id === targetId ? groupAnchor : f.groupKey) ?? null,
      groupKind: f.groupKind ?? null,
      groupTitle: f.groupTitle ?? null,
    }));
    if (groupAnchor != null) {
      // Grouping: ask the PM whether it's the same content or just a
      // logical bundle BEFORE saving — the answer drives the client view.
      const memberIds = new Set(order.filter(o => o.groupKey === groupAnchor).map(o => o.id));
      // Prefill with the group's existing custom title (if merging into one).
      setPendingGroupTitle(files.find(f => memberIds.has(f.id) && f.groupTitle)?.groupTitle || "");
      setPendingGroup({
        order,
        anchor: groupAnchor,
        names: files.filter(f => memberIds.has(f.id)).map(f => f.title || f.fileName),
      });
      return;
    }
    reorderMutation.mutate(order);
  };

  /** PM chose the meaning of the pending group — save it. */
  const confirmGroup = (kind: 'same_content' | 'related') => {
    if (!pendingGroup) return;
    const title = pendingGroupTitle.trim().slice(0, 255) || null;
    if (!confirmUnnamedGroupCreation(title || '')) {
      return;
    }
    reorderMutation.mutate(pendingGroup.order.map(o => ({
      ...o,
      groupKind: o.groupKey === pendingGroup.anchor ? kind : o.groupKind ?? null,
      groupTitle: o.groupKey === pendingGroup.anchor ? title : o.groupTitle ?? null,
    })));
    setPendingGroup(null);
    setPendingGroupTitle("");
  };

  /** Remove one file from its format group (order unchanged). */
  const ungroupFile = (fileId: number) => {
    if (!share) return;
    reorderMutation.mutate(share.files.map((f) => ({
      id: f.id,
      groupKey: f.id === fileId ? null : f.groupKey ?? null,
      groupKind: f.id === fileId ? null : f.groupKind ?? null,
      groupTitle: f.id === fileId ? null : f.groupTitle ?? null,
    })));
  };

  const patchFileMutation = useMutation({
    mutationFn: async ({ fileId, title, description }: { fileId: number; title: string; description: string }) =>
      fetchJson<ShareInfo>(`/api/preview-shares/${share!.id}/files/${fileId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, description }),
      }),
    onSuccess: (data) => {
      if (createdShare) setCreatedShare(data);
      invalidateShare();
      setEditingFileId(null);
      toast({ title: "File updated" });
    },
    onError: (err: any) => toast({ title: "Could not update file", description: err?.message, variant: "destructive" }),
  });

  const toggleSelect = (entry: DropboxEntry) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(entry.path)) next.delete(entry.path);
      else next.set(entry.path, entry);
      return next;
    });
    setFileMeta((prev) => {
      const next = new Map(prev);
      if (next.has(entry.path)) next.delete(entry.path);
      else next.set(entry.path, { title: cleanFileTitle(entry.name), description: "" });
      return next;
    });
  };

  const setMeta = (path: string, patch: Partial<{ title: string; description: string }>) => {
    setFileMeta((prev) => {
      const next = new Map(prev);
      const cur = next.get(path) || { title: "", description: "" };
      next.set(path, { ...cur, ...patch });
      return next;
    });
  };

  const selectedFilesPayload = () =>
    Array.from(selected.values()).map((f) => {
      const meta = fileMeta.get(f.path);
      return {
        path: f.path,
        title: meta?.title?.trim() || undefined,
        description: meta?.description?.trim() || undefined,
      };
    });

  const copyLink = async () => {
    if (!share?.urlPath) return;
    const url = `${window.location.origin}${share.urlPath}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied", description: url });
    } catch {
      toast({ title: "Copy failed", description: url, variant: "destructive" });
    }
  };

  // Task #189: resolve share type for manage mode from the loaded share.
  const manageShareType = share?.shareType ?? 'preview';
  const isFinalManage = manageMode && manageShareType === 'final';
  // Color / icon helpers keyed by share type.
  const shareTypeLabel = (type: 'preview' | 'final') => type === 'final' ? 'Final' : 'Preview';
  const shareTypeColor = (type: 'preview' | 'final') => type === 'final' ? '#34d391' : '#5f8ac9';
  const createTypeLabel = shareTypeLabel(effectiveCreateType);
  const createTypeColor = shareTypeColor(effectiveCreateType);
  const allShareFilesReady = !!share && share.files.length > 0 && share.files.every(f => {
    if (f.deliveryMode === "dropbox_direct") return true;
    if (f.transferStatus !== "completed") return false;
    const isPsd = /\.(psd|psb)$/i.test(f.fileName);
    if (isPsd && !f.hasPreview && f.previewStatus !== "completed" && f.previewStatus !== "failed") return false;
    return f.previewStatus !== "pending";
  });
  const shareEmailDraft = useMemo(() => {
    if (!share?.urlPath || !effectiveProjectId || !effectiveProjectName) return null;
    return buildShareEmailDraft({
      shareType: manageShareType,
      projectId: effectiveProjectId,
      projectName: effectiveProjectName,
      shareTitle: share.title,
      senderName,
      url: `${window.location.origin}${share.urlPath}`,
      previewShareId: share.id,
    });
  }, [effectiveProjectId, effectiveProjectName, manageShareType, senderName, share?.urlPath, share?.id, share?.title]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        // The nested grouping dialog owns dismissal while it is active.
        if (!nextOpen && pendingGroup) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        className="flex flex-col w-[96vw] max-w-[96vw] sm:w-[92vw] sm:max-w-[1400px] h-[92vh] max-h-[92vh] navy-card border-gray-600 text-white overflow-hidden p-6"
        data-testid="dialog-preview-share"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-white flex items-center gap-2">
            <Eye className="h-5 w-5" style={{ color: manageMode ? shareTypeColor(manageShareType) : createTypeColor }} />
            {manageMode
              ? `Client ${shareTypeLabel(manageShareType)} Share`
              : `Share a ${createTypeLabel} with the Client`}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {manageMode
              ? `Manage the secure link your client uses to view and download these files.`
              : effectiveCreateType === 'final'
                ? "Create the official Final delivery share. If an active Final exists it will be converted to a Preview first."
                : "Pick files from the project's Dropbox folder. The client gets a fast, secure page — no Dropbox access needed."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4">

        {/* ---------- MANAGE ---------- */}
        {manageMode && (
          <div className="space-y-4">
            {shareLoading && !share && (
              <div className="flex items-center justify-center py-8 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading share…
              </div>
            )}
            {shareIsError && !share && (
              <Alert className="bg-red-950/40 border-red-800">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-red-300">No share found for this pulse.</AlertDescription>
              </Alert>
            )}
            {share && (
              <>
                {share.revokedAt ? (
                  <Alert className="bg-red-950/40 border-red-800 py-2">
                    <AlertDescription className="text-red-300 text-sm">
                      This share was revoked — the link no longer works.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-2 bg-gray-800/60 border border-gray-700 rounded px-3 py-2 text-sm text-gray-300 min-w-0">
                      <Link2 className="h-4 w-4 shrink-0" style={{ color: shareTypeColor(manageShareType) }} />
                      {isFinalManage && (
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded mr-1 shrink-0" style={{ color: '#34d391', background: '#34d39122' }}>FINAL</span>
                      )}
                      <span className="truncate">{window.location.origin}{share.urlPath}</span>
                    </div>
                    {(() => {
                      // Green = every file fully delivered INCLUDING derived
                      // previews (PSD → PNG) — the link is completely ready.
                      return (
                        <Button
                          size="sm"
                          onClick={copyLink}
                          className={allShareFilesReady ? "bg-emerald-600 hover:bg-emerald-500 text-white" : undefined}
                          title={allShareFilesReady ? "All files & previews ready" : "Files are still being prepared"}
                          data-testid="button-copy-link"
                        >
                          <Copy className="h-4 w-4 mr-1" /> Copy
                        </Button>
                      );
                    })()}
                    {share.urlPath && <Button
                      size="sm"
                      variant="outline"
                      className="border-gray-600 text-gray-200 hover:bg-gray-700 shrink-0"
                      asChild
                    >
                      <a
                        href={share.urlPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View ${shareTypeLabel(manageShareType)} in a new tab`}
                        data-testid="view-share-link"
                      >
                        <Eye className="h-4 w-4 mr-1" /> View
                      </a>
                    </Button>}
                  </div>
                )}

                 {/* A saved URL is enough to begin composing. The server's
                     share-send check owns final readiness, so pending
                     transfers must not hide the editable composer. Keep the
                     revoked guard here: revoked links are never offered for
                     delivery. */}
                 {!share.revokedAt && shareEmailDraft && effectiveProjectId && (
                  <div
                    className="rounded-lg border border-blue-800/70 bg-blue-950/30 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                    data-testid="send-share-link-section"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm font-semibold text-blue-200">
                        <Send className="h-4 w-4" />
                        Send this {shareTypeLabel(manageShareType)} link directly to the client
                      </div>
                       <p className="mt-1 text-xs text-gray-400">
                        Uses the same recipients, previous contact groups, sending accounts and email workflow as Mail Out.
                        The prepared message is completely editable before sending.
                      </p>
                       {!allShareFilesReady && (
                         <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-300" data-testid="share-email-readiness-hint">
                           <Clock className="h-3.5 w-3.5 shrink-0" />
                           Files are still being prepared. You can compose now; sending will check their readiness.
                         </p>
                       )}
                    </div>
                    <Button
                      type="button"
                      onClick={() => setSendLinkOpen(true)}
                      className="bg-blue-600 hover:bg-blue-500 text-white shrink-0"
                      data-testid="button-send-share-link"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Compose email
                    </Button>
                  </div>
                )}

                {/* Expiry + password + recipient */}
                {!share.revokedAt && (
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Link works until{inDaysSuffix(share.expiresAt)}</label>
                      <Input
                        type="date"
                        min={minExpiryDate()}
                        className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                        defaultValue={share.expiresAt?.slice(0, 10)}
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v && v >= minExpiryDate() && v !== share.expiresAt?.slice(0, 10)) {
                            patchMutation.mutate({ expiresAt: new Date(`${v}T23:59:59`).toISOString() });
                          }
                        }}
                        data-testid="input-expiry"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">
                        Password {share.hasPassword ? "(set)" : "(none)"}
                      </label>
                      <div className="flex gap-1">
                        <Input
                          type="text"
                          placeholder={share.hasPassword ? "New password…" : "Add password…"}
                          className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          data-testid="input-password"
                        />
                        <Button
                          size="sm"
                          className="h-8"
                          disabled={!password || patchMutation.isPending}
                          onClick={() => { patchMutation.mutate({ password }); setPassword(""); }}
                        >
                          <Lock className="h-3.5 w-3.5" />
                        </Button>
                        {share.hasPassword && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 border-gray-600"
                            disabled={patchMutation.isPending}
                            onClick={() => patchMutation.mutate({ password: null })}
                            title="Remove password"
                          >
                            <Unlock className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Client (name shown in chat)</label>
                      <select
                        className="w-full h-8 text-sm rounded-md bg-gray-800 border border-gray-700 text-white px-2"
                        value={share.recipientContactId ?? ""}
                        disabled={patchMutation.isPending}
                        onChange={(e) => patchMutation.mutate({ recipientContactId: e.target.value || null })}
                        data-testid="select-manage-recipient"
                      >
                        <option value="">No name (shown as "Kunde")</option>
                        {contactOptions.map((c) => (
                          <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {!share.revokedAt && share.shareType !== 'final' && (
                  <label className="flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
                    <input type="checkbox" className="mt-1" checked={share.enableClientUploads === true}
                      disabled={patchMutation.isPending}
                      onChange={event => patchMutation.mutate({ enableClientUploads: event.target.checked })}
                      data-testid="checkbox-manage-client-uploads" />
                    <span>Kundendateien empfangen <span className="block text-xs text-gray-400">Preview-Seite: direkter Upload mit interner Freigabe. Gilt auch für abgeleitete Links.</span></span>
                  </label>
                )}
                {/* White-label re-sharing toggle (client branding) */}
                {!share.revokedAt && (
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={share.allowClientBranding === true}
                      disabled={patchMutation.isPending}
                      onChange={(e) => patchMutation.mutate({ allowClientBranding: e.target.checked })}
                      data-testid="checkbox-manage-client-branding"
                    />
                    Client-Branding — client may create their own branded links from this page
                  </label>
                )}

                {/* Download restriction never silently removes delivered assets. */}
                {!share.revokedAt && (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={share.disableDownloads === true || manageDownloadRestrictionRequested}
                        disabled={patchMutation.isPending}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            setManageDownloadRestrictionRequested(true);
                          } else {
                            setManageDownloadRestrictionRequested(false);
                            if (checked !== (share.disableDownloads === true)) {
                              patchMutation.mutate({ disableDownloads: checked });
                            }
                          }
                        }}
                        data-testid="checkbox-manage-disable-downloads"
                      />
                      Disable downloads — delivery files only (chat attachments are unaffected)
                    </label>
                    {manageDownloadRestrictionRequested && share.files.length > 0 && (
                      <MediaInspectionStatus inspection={deliveredInspection} testId="manage-download-policy-preflight" />
                    )}
                    {(manageDownloadRestrictionRequested || (share.disableDownloads === true && incompatibleDelivered.length > 0)) && (
                      <Alert className="bg-amber-950/30 border-amber-800 py-2" data-testid="alert-manage-download-incompatibilities">
                        <AlertDescription className="text-amber-300 text-xs space-y-2">
                          {incompatibleDelivered.length > 0 ? (
                            <>
                              <p>
                                Downloads cannot be disabled until every delivery file has a usable browser preview.
                                Video/audio must pass actual-encoding inspection; pending, failed, and unverified checks block saving.
                                Master formats (such as ProRes/MOV and MXF), documents, archives, and files over {previewLimitGiB} GiB are not supported.
                                No delivered files have been removed. Use each file's Remove button below, or leave downloads enabled.
                              </p>
                              <ul className="list-disc pl-4">
                                {incompatibleDelivered.map(f => <li key={f.id}>{f.fileName} — {deliveredOutcome(f).reason}</li>)}
                              </ul>
                            </>
                          ) : <p>All remaining files are verified for browser preview. Apply the restriction when you are ready.</p>}
                          {manageDownloadRestrictionRequested && (
                            <Button
                              type="button"
                              size="sm"
                              disabled={incompatibleDelivered.length > 0 || patchMutation.isPending}
                              onClick={() => patchMutation.mutate({ disableDownloads: true })}
                              data-testid="button-apply-disable-downloads"
                            >
                              Enable disable downloads
                            </Button>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}

                {/* Broken direct-link warning (task #78) */}
                {!share.revokedAt && missingFileIds.size > 0 && (
                  <Alert className="bg-amber-950/30 border-amber-800 py-2" data-testid="alert-missing-files">
                    <AlertTriangle className="h-4 w-4 text-amber-400" />
                    <AlertDescription className="text-amber-300 text-xs flex items-center gap-2">
                      <span className="flex-1">
                        {missingFileIds.size === 1
                          ? "1 file was renamed or deleted in Dropbox after sharing — its download link is broken for the client."
                          : `${missingFileIds.size} files were renamed or deleted in Dropbox after sharing — their download links are broken for the client.`}
                        {" "}Remove the affected file{missingFileIds.size === 1 ? "" : "s"} and re-add {missingFileIds.size === 1 ? "it" : "them"} from the new location.
                      </span>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-amber-300 hover:text-white shrink-0"
                        onClick={() => recheckFiles()} disabled={verifying} data-testid="button-recheck-files">
                        <RefreshCw className={`h-3 w-3 mr-1 ${verifying ? "animate-spin" : ""}`} /> Re-check
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Verification couldn't run (task #81): neutral hint, not a warning */}
                {!share.revokedAt && unknownFileCount > 0 && (
                  <Alert className="bg-gray-800/40 border-gray-700 py-2" data-testid="alert-unverified-files">
                    <Info className="h-4 w-4 text-gray-400" />
                    <AlertDescription className="text-gray-400 text-xs flex items-center gap-2">
                      <span className="flex-1">
                        Couldn't check {unknownFileCount === 1 ? "one file" : "some files"} in Dropbox right now — the links are probably fine, re-check in a moment.
                      </span>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-gray-400 hover:text-white shrink-0"
                        onClick={() => recheckFiles()} disabled={verifying} data-testid="button-recheck-unverified">
                        <RefreshCw className={`h-3 w-3 mr-1 ${verifying ? "animate-spin" : ""}`} /> Re-check
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Files — drag rows to reorder; drop ONTO a row to group
                    both files as one content in several formats. */}
                {share.files.length > 1 && !share.revokedAt && (
                  <p className="text-[11px] text-gray-500 -mb-2">
                    Drag to reorder — drop onto another file to group them.
                  </p>
                )}
                <Dialog
                  open={!!pendingGroup}
                  onOpenChange={(groupOpen) => {
                    if (!groupOpen) cancelPendingGroup();
                  }}
                >
                  <DialogPrimitive.Portal>
                    <DialogPrimitive.Overlay
                      className="fixed inset-0 z-[60] bg-black/80 pointer-events-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                      data-testid="grouping-dialog-overlay"
                    />
                    <DialogPrimitive.Content
                      className="fixed left-[50%] top-[50%] z-[61] grid grid-cols-[minmax(0,1fr)] w-[440px] max-w-[92vw] max-h-[90dvh] overflow-y-auto overflow-x-hidden translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border border-gray-700 bg-gray-900 p-5 shadow-xl pointer-events-auto duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
                      data-testid="grouping-dialog"
                      onPointerDownOutside={(event) => event.preventDefault()}
                      onOpenAutoFocus={(event) => {
                        event.preventDefault();
                        pendingGroupTitleRef.current?.focus();
                      }}
                    >
                    <DialogHeader className="min-w-0 space-y-1">
                      <DialogTitle className="text-sm text-white">How are these files related?</DialogTitle>
                      <DialogDescription
                        className="min-w-0 max-h-24 overflow-y-auto whitespace-normal [overflow-wrap:anywhere] text-xs text-gray-400"
                        data-testid="grouping-file-names"
                        tabIndex={0}
                      >
                        {pendingGroup?.names.join("  ·  ")}
                      </DialogDescription>
                    </DialogHeader>
                      <div className="mb-4">
                        <label className="text-xs text-gray-400 block mb-1" htmlFor="group-title-input">
                          Group title <span className="text-gray-500">(optional — used on the client group-download button and as the ZIP filename)</span>
                        </label>
                        <Input
                          ref={pendingGroupTitleRef}
                          id="group-title-input"
                          value={pendingGroupTitle}
                          onChange={(e) => setPendingGroupTitle(e.target.value)}
                          placeholder="No custom group name"
                          maxLength={255}
                          className="h-8 text-sm"
                          data-testid="input-group-title"
                        />
                      </div>
                      <div className="space-y-2">
                        <button
                          type="button"
                          className="w-full text-left rounded-md border border-sky-700/60 bg-sky-950/40 hover:bg-sky-900/40 p-3 transition-colors"
                          data-testid="button-group-same-content"
                          onClick={() => confirmGroup('same_content')}
                        >
                          <div className="text-sm font-medium text-sky-200">Same content — different formats</div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            One asset in several encodings (e.g. MP4 + ProRes). The client page shows them as ONE
                            content with format options: direct “watch as MP4” shortcut on download-only masters
                            and alternative formats in the download menu (only when duration & aspect ratio match).
                          </div>
                        </button>
                        <button
                          type="button"
                          className="w-full text-left rounded-md border border-gray-700 bg-gray-800/60 hover:bg-gray-800 p-3 transition-colors"
                          data-testid="button-group-related"
                          onClick={() => confirmGroup('related')}
                        >
                          <div className="text-sm font-medium text-gray-200">Just related files</div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            A logical bundle of different content (e.g. a video plus its poster frame). Shown as a
                            visual group on the client page — each file keeps its own preview and download, no
                            cross-format shortcuts.
                          </div>
                        </button>
                      </div>
                      <div className="mt-3 text-right">
                        <Button size="sm" variant="ghost" data-testid="button-group-cancel" onClick={cancelPendingGroup}>
                          Cancel
                        </Button>
                      </div>
                    </DialogPrimitive.Content>
                  </DialogPrimitive.Portal>
                </Dialog>
                <div className="border border-gray-700 rounded divide-y divide-gray-800 max-h-[40vh] overflow-y-auto">
                  {(() => { const groupColors = computeGroupColors(share.files); return share.files.map((f) => {
                    const grpColor = groupColors.get(f.id);
                    const hint = dropHint?.id === f.id ? dropHint.pos : null;
                    return (
                    <div
                      key={f.id}
                      data-testid={`share-file-${f.id}`}
                      className={[
                        missingFileIds.has(f.id) ? "bg-amber-950/20" : "",
                        dragFileId === f.id ? "opacity-40" : "",
                        hint === "group" ? "ring-2 ring-inset ring-sky-400/80 bg-sky-950/30" : "",
                        hint === "before" ? "shadow-[inset_0_2px_0_0_#38bdf8]" : "",
                        hint === "after" ? "shadow-[inset_0_-2px_0_0_#38bdf8]" : "",
                        "transition-colors",
                      ].filter(Boolean).join(" ")}
                      style={grpColor ? { borderLeft: `3px solid ${grpColor}`, backgroundColor: `${grpColor}14` } : undefined}
                      draggable={!share.revokedAt}
                      onDragStart={(e) => { setDragFileId(f.id); e.dataTransfer.effectAllowed = "move"; }}
                      onDragEnd={() => { setDragFileId(null); setDropHint(null); }}
                      onDragOver={(e) => {
                        if (dragFileId == null || dragFileId === f.id) return;
                        e.preventDefault();
                        const r = e.currentTarget.getBoundingClientRect();
                        const y = e.clientY - r.top;
                        const pos: DropHint["pos"] = y < r.height * 0.28 ? "before" : y > r.height * 0.72 ? "after" : "group";
                        setDropHint((h) => (h?.id === f.id && h.pos === pos ? h : { id: f.id, pos }));
                      }}
                      onDragLeave={() => setDropHint((h) => (h?.id === f.id ? null : h))}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragFileId != null && dropHint?.id === f.id) applyDrop(dragFileId, f.id, dropHint.pos);
                        setDragFileId(null); setDropHint(null);
                      }}
                    >
                      <div className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-200">
                        {!share.revokedAt && (
                          <GripVertical className="h-4 w-4 text-gray-600 shrink-0 cursor-grab" aria-label="Drag to reorder" />
                        )}
                        {missingFileIds.has(f.id)
                          ? <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" aria-label="File missing in Dropbox" />
                          : <TransferStatusIcon file={f} />}
                        <ProtectedFileThumb shareId={share.id} file={f} />
                        <span className="truncate flex-1">
                          {f.title || f.fileName}
                          {f.description && <span className="text-gray-500"> — {f.description}</span>}
                        </span>
                        {grpColor && !share.revokedAt && (
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 shrink-0"
                            style={{ color: grpColor }}
                            title="Remove from format group"
                            data-testid={`button-ungroup-${f.id}`}
                            onClick={() => ungroupFile(f.id)}>
                            <Ungroup className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <FileFormatBadge fileName={f.fileName} />
                        <OriginBadge label={f.originLabel} />
                        <span className="text-xs text-gray-500 w-16 text-right">{formatBytes(f.sizeBytes)}</span>
                        {!share.revokedAt && (
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-gray-500 hover:text-white"
                            title="Edit title & description"
                            data-testid={`button-edit-file-${f.id}`}
                            onClick={() => {
                              if (editingFileId === f.id) { setEditingFileId(null); return; }
                              setEditingFileId(f.id);
                              setEditDraft({ title: f.title || cleanFileTitle(f.fileName), description: f.description || "" });
                            }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {f.transferStatus === "failed" && !share.revokedAt && (
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-amber-300" title={f.transferError || "Retry"}
                            onClick={() => retryMutation.mutate(f.id)}>
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {!share.revokedAt && (
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-gray-500 hover:text-red-400"
                            title={`Remove ${f.fileName}`}
                            aria-label={`Remove ${f.fileName}`}
                            data-testid={`button-remove-file-${f.id}`}
                            onClick={() => removeFileMutation.mutate(f.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      {editingFileId === f.id && (
                        <div className="px-3 pb-2 space-y-1.5 bg-gray-900/40">
                          <Input
                            className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                            value={editDraft.title}
                            placeholder="Title shown to the client"
                            onChange={(e) => setEditDraft((d) => ({ ...d, title: e.target.value }))}
                            data-testid={`input-edit-title-${f.id}`}
                          />
                          <Input
                            className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                            value={editDraft.description}
                            placeholder="Optional description"
                            onChange={(e) => setEditDraft((d) => ({ ...d, description: e.target.value }))}
                            data-testid={`input-edit-desc-${f.id}`}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" className="h-7 text-xs"
                              disabled={patchFileMutation.isPending}
                              onClick={() => patchFileMutation.mutate({ fileId: f.id, title: editDraft.title, description: editDraft.description })}
                              data-testid={`button-save-file-${f.id}`}>
                              {patchFileMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-400"
                              onClick={() => setEditingFileId(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ); }); })()}
                  {share.files.length === 0 && (
                    <div className="p-4 text-center text-xs text-gray-500">No files in this share</div>
                  )}
                </div>

                {/* Client-created white-label links (Weitergaben des Kunden) */}
                {(share.children?.length ?? 0) > 0 && (
                  <div className="border border-gray-700 rounded" data-testid="children-list">
                    <div className="px-3 py-1.5 text-xs text-gray-400 border-b border-gray-800">
                      Weitergaben des Kunden ({share.children!.length})
                    </div>
                    <div className="divide-y divide-gray-800 max-h-[25vh] overflow-y-auto">
                      {share.children!.map((c) => (
                        <div key={c.id} className="px-3 py-2 text-xs text-gray-300 flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="truncate">
                              {c.files.length} file{c.files.length === 1 ? "" : "s"}: {c.files.map((f) => f.title).join(", ") || "—"}
                            </div>
                            <div className="text-gray-500 mt-0.5">
                              Created {new Date(c.createdAt).toLocaleDateString("de-DE")}
                              {c.hasPassword && " · password"}
                              {[c.showChat && "Chat", c.showTeam && "Team", c.showDates && "Termine"].filter(Boolean).length > 0 &&
                                ` · ${[c.showChat && "Chat", c.showTeam && "Team", c.showDates && "Termine"].filter(Boolean).join(", ")}`}
                            </div>
                          </div>
                          {Number.isSafeInteger(c.externalVisitCount) && c.externalVisitCount >= 0 ? (
                            <span
                              className="text-gray-400 shrink-0"
                              title={`Client page opens: ${c.externalVisitCount}. ${
                                c.visitsCountedSince && Number.isFinite(new Date(c.visitsCountedSince).getTime())
                                  ? `Counted since ${new Date(c.visitsCountedSince).toLocaleString()}.`
                                  : "Counting start unavailable."
                              } Successful page opens, not unique visitors.`}
                              data-testid={`child-share-visit-count-${c.id}`}
                            >
                              <Eye className="inline w-3 h-3 mr-0.5" aria-hidden="true" />
                              {c.externalVisitCount}× viewed
                            </span>
                          ) : (
                            <span className="text-amber-400 shrink-0" title="Client page open count unavailable">
                              Visits unavailable
                            </span>
                          )}
                          {c.revokedAt ? (
                            <span className="text-red-400 shrink-0">revoked</span>
                          ) : (
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-400 hover:bg-red-950/40 shrink-0"
                              onClick={() => {
                                if (confirm("Revoke this client link? It stops working immediately.")) {
                                  fetchJson(`/api/preview-shares/${c.id}/revoke`, { method: "POST" })
                                    .then(() => invalidateShare())
                                    .catch((err: any) => toast({ title: "Revoke failed", description: err?.message, variant: "destructive" }));
                                }
                              }}
                              data-testid={`button-revoke-child-${c.id}`}>
                              Revoke
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Activity protocol — chronological history of the client page */}
                <ActivityProtocol
                  pulseId={share.pulseId}
                  shareId={share.id}
                  onViewFile={(sid, fileId, fileName) => {
                    // Popup-safe: open the tab synchronously, then navigate it.
                    const win = window.open("", "_blank");
                    fetchJson<{ url: string }>(`/api/preview-shares/${sid}/files/${fileId}/view-link`, { method: "POST" })
                      .then(({ url }) => { if (win) win.location.href = url; })
                      .catch((err: any) => {
                        win?.close();
                        toast({
                          title: "Datei nicht mehr verfügbar",
                          description: err?.message || `"${fileName}" ist nicht mehr im Speicher.`,
                          variant: "destructive",
                        });
                      });
                  }}
                />

                {/* Add files / revoke */}
                {!share.revokedAt && (
                  <div className="flex items-center justify-between">
                    <Button size="sm" variant="outline" className="border-gray-600 text-gray-300"
                      onClick={() => setAddingFiles((v) => !v)} data-testid="button-add-files">
                      <Plus className="h-4 w-4 mr-1" /> Add files
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-800 text-red-400 hover:bg-red-950/40"
                      disabled={revokeMutation.isPending}
                      onClick={() => { if (confirm("Revoke this share? The client link stops working immediately.")) revokeMutation.mutate(); }}
                      data-testid="button-revoke">
                      Revoke link
                    </Button>
                  </div>
                )}

                {addingFiles && effectiveProjectId && (
                  <div className="space-y-2">
                    <SharedFilePicker projectId={effectiveProjectId} selected={selected} onToggle={toggleSelect} />
                    {share.disableDownloads === true && selected.size > 0 && (
                      <MediaInspectionStatus inspection={prospectiveInspection} testId="add-download-policy-preflight" />
                    )}
                    {addingBlockedByDownloads && (
                      <Alert className="bg-amber-950/30 border-amber-800 py-2" data-testid="alert-add-download-incompatibilities">
                        <AlertDescription className="text-amber-300 text-xs space-y-2">
                          <p>This share disables downloads. Only browser-previewable files up to {previewLimitGiB} GiB with verified actual encoding can be added. Chat attachments are unaffected.</p>
                          <ul className="list-disc pl-4">{incompatibleSelected.map(f => <li key={f.path}>{f.name} — {selectedOutcome(f).reason}</li>)}</ul>
                          <Button type="button" size="sm" onClick={removeAllIncompatibleSelections} data-testid="button-remove-all-non-viewable">
                            Remove ALL non-viewable or unverified files from selection
                          </Button>
                        </AlertDescription>
                      </Alert>
                    )}
                    <Button size="sm" disabled={selected.size === 0 || addFilesMutation.isPending || addingBlockedByDownloads}
                      data-testid="button-confirm-add-files"
                      onClick={() => addFilesMutation.mutate()}>
                      {addFilesMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                      Add {selected.size} selected
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ---------- CREATE ---------- */}
        {!manageMode && projectId && (
          <div className="space-y-4">

            {/* Task #189: Final replacement blocking panel */}
            {isFinalCreate && activeFinalLoading && (
              <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking for active Final share…
              </div>
            )}
            {isFinalCreate && !activeFinalLoading && activeFinalShare && !finalReplaceConfirmed && (
              <div
                className="rounded-lg border border-[#34d391]/40 bg-[#34d391]/5 p-4 space-y-3"
                data-testid="final-replacement-panel"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-[#34d391] shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-[#34d391]">An active Final share already exists</p>
                    <p className="text-xs text-gray-300">
                      Creating a new Final will convert the existing one to a Preview share.
                      The old link and all of its current files stay intact as a Preview.
                      You can additionally copy selected assets into the new Final below.
                    </p>
                    <p className="text-xs text-gray-400 font-medium">
                      Existing Final: <span className="text-white">{activeFinalShare.title}</span>
                      {" "}· expires {new Date(activeFinalShare.expiresAt).toLocaleDateString("de-DE")}
                    </p>
                  </div>
                </div>

                {/* Selectable old Final files to copy into the new Final */}
                {activeFinalShare.files.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-gray-400">Select old assets to copy into the new Final:</p>
                    <div className="border border-gray-700 rounded divide-y divide-gray-800 max-h-[20vh] overflow-y-auto">
                      {activeFinalShare.files.map((f) => (
                        <label key={f.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer text-gray-200 hover:bg-gray-800/40">
                          <input
                            type="checkbox"
                            checked={carryOverFileIds.has(f.id)}
                            onChange={() => {
                              setCarryOverFileIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(f.id)) next.delete(f.id); else next.add(f.id);
                                return next;
                              });
                            }}
                            className="accent-emerald-400"
                            data-testid={`carryover-file-${f.id}`}
                          />
                          <ProtectedFileThumb shareId={activeFinalShare.id} file={f} />
                          <span className="truncate flex-1">{f.title || f.fileName}</span>
                          <span className="text-xs text-gray-500 shrink-0">{formatBytes(f.sizeBytes)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resulting Preview expiry */}
                <div className="flex items-center gap-3">
                  <label className="text-xs text-gray-400 shrink-0">Resulting Preview expires{inDaysSuffix(oldFinalPreviewExpiry)}:</label>
                  <Input
                    type="date"
                    min={minExpiryDate()}
                    className="bg-gray-800 border-gray-700 text-white h-8 text-sm w-40"
                    value={oldFinalPreviewExpiry}
                    onChange={(e) => setOldFinalPreviewExpiry(e.target.value)}
                    data-testid="input-oldfinal-preview-expiry"
                  />
                </div>

                {/* Dropbox HISTORY reminder */}
                <Alert className="bg-amber-950/20 border-amber-800/50 py-2">
                  <AlertDescription className="text-amber-300/90 text-xs">
                    <strong>Dropbox HISTORY reminder:</strong> After creating the replacement, move the superseded
                    versions from the Dropbox <strong>FINAL</strong> folder into a <strong>HISTORY</strong> folder.
                    The app will not move any Dropbox files automatically.
                  </AlertDescription>
                </Alert>

                <div className="flex items-center gap-2 pt-1">
                  <Button
                    size="sm"
                    style={{ background: '#34d391', color: '#0a1a12' }}
                    className="hover:opacity-90"
                    onClick={() => setFinalReplaceConfirmed(true)}
                    data-testid="button-confirm-replace-final"
                  >
                    I understand — proceed with replacement
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-gray-400"
                    onClick={() => onOpenChange(false)}
                    data-testid="button-cancel-replace-final"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Show create form only if not blocking on Final replacement confirmation */}
            {(!isFinalCreate || !activeFinalShare || finalReplaceConfirmed) && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Title</label>
                    <Input className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                      value={title} onChange={(e) => setTitle(e.target.value)} data-testid="input-title" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Client (name shown in chat)</label>
                    <select
                      className="w-full h-8 text-sm rounded-md bg-gray-800 border border-gray-700 text-white px-2"
                      value={recipientContactId}
                      onChange={(e) => setRecipientContactId(e.target.value)}
                      data-testid="select-recipient"
                    >
                      <option value="">No name (shown as "Kunde")</option>
                      {contactOptions.map((c) => (
                        <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Password (optional)</label>
                    <Input type="text" placeholder="None" className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                      value={password} onChange={(e) => setPassword(e.target.value)} data-testid="input-create-password" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Link works until{inDaysSuffix(expiresAt)}</label>
                    <Input type="date" min={minExpiryDate()} className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                      value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} data-testid="input-create-expiry" />
                  </div>
                </div>

                {/* Optional page sections on the client page. */}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <span className="text-xs text-gray-400">Page sections:</span>
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showChat}
                      onChange={(e) => setShowChat(e.target.checked)}
                      data-testid="checkbox-show-chat"
                    />
                    Chat
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showTeam}
                      onChange={(e) => setShowTeam(e.target.checked)}
                      data-testid="checkbox-show-team"
                    />
                    Project Team (video calls)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showDates}
                      onChange={(e) => setShowDates(e.target.checked)}
                      data-testid="checkbox-show-dates"
                    />
                    Termine
                  </label>
                  {!isFinalCreate && (
                    <label className="flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
                      <input type="checkbox" className="mt-1" checked={enableClientUploads}
                        onChange={event => setEnableClientUploads(event.target.checked)}
                        data-testid="checkbox-client-uploads" />
                      <span>Kundendateien empfangen <span className="block text-xs text-gray-400">Upload-Bereich auf der Preview-Seite. Jede bestätigte Datei braucht interne Freigabe.</span></span>
                    </label>
                  )}
                  {isFinalCreate && (
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showClientProjects}
                        onChange={(e) => setShowClientProjects(e.target.checked)}
                        data-testid="checkbox-show-client-projects"
                      />
                      Ihre bisherigen Projekte
                    </label>
                  )}
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allowClientBranding}
                      onChange={(e) => setAllowClientBranding(e.target.checked)}
                      data-testid="checkbox-client-branding"
                    />
                    Client-Branding (client may re-share)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={disableDownloads}
                      onChange={(e) => setDisableDownloads(e.target.checked)}
                      data-testid="checkbox-disable-downloads"
                    />
                    Disable downloads
                  </label>
                </div>
                {disableDownloads && (
                  <p className="text-xs text-gray-400">
                    Viewing only: every delivery file must support browser preview (up to {previewLimitGiB} GiB).
                    Video/audio must pass actual-encoding inspection before creation.
                    Chat messages and attachments are unaffected. This does not prevent screen recording or copying viewed content.
                  </p>
                )}

                <SharedFilePicker projectId={projectId} selected={selected} onToggle={toggleSelect} />

                {disableDownloads && (selected.size > 0 || carryOverSelected.length > 0) && (
                  <MediaInspectionStatus inspection={prospectiveInspection} testId="download-policy-preflight" />
                )}
                {creationBlockedByDownloads && (
                  <Alert className="bg-amber-950/30 border-amber-800 py-2" data-testid="alert-download-incompatibilities">
                    <AlertDescription className="text-amber-300 text-xs space-y-2">
                      <p>
                        This share cannot be created with downloads disabled: ALL selected files must have a usable browser preview.
                        Pending, failed, unsupported, or unverified media inspections also block creation.
                        ProRes/MOV, MXF, documents, archives, unsupported formats, and files over {previewLimitGiB} GiB cannot be viewed here.
                        Retry inspection, remove the files below, or uncheck Disable downloads.
                        Removal includes files still awaiting verification; it never deletes originals.
                      </p>
                      <ul className="list-disc pl-4">
                        {incompatibleSelected.map(f => <li key={f.path}>{f.name} — {selectedOutcome(f).reason}</li>)}
                        {incompatibleCarryOver.map(f => <li key={f.id}>{f.fileName} (selected copy from old Final) — {carryOverOutcome(f).reason}</li>)}
                      </ul>
                      <Button
                        type="button"
                        size="sm"
                        onClick={removeAllIncompatibleSelections}
                        data-testid="button-remove-all-non-viewable"
                      >
                        Remove ALL non-viewable or unverified files from selection
                      </Button>
                      {incompatibleCarryOver.length > 0 && <p>Only copies are deselected; files on the existing Final remain intact.</p>}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Per-file title & description shown on the client page (task #73). */}
                {selected.size > 0 && (
                  <div className="border border-gray-700 rounded divide-y divide-gray-800">
                    <div className="px-3 py-1.5 text-xs text-gray-400">
                      Titles &amp; descriptions shown to the client
                    </div>
                    {Array.from(selected.values()).map((f) => {
                      const meta = fileMeta.get(f.path) || { title: cleanFileTitle(f.name), description: "" };
                      return (
                        <div key={f.path} className="px-3 py-2 space-y-1.5" data-testid={`meta-${f.name}`}>
                          <div className="flex items-center gap-2">
                            <FileIcon className="h-3.5 w-3.5 text-gray-500 shrink-0" />
                            <span className="text-xs text-gray-500 truncate flex-1">{f.name}</span>
                          </div>
                          <Input
                            className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                            value={meta.title}
                            placeholder="Title shown to the client"
                            onChange={(e) => setMeta(f.path, { title: e.target.value })}
                            data-testid={`input-file-title-${f.name}`}
                          />
                          <Input
                            className="bg-gray-800 border-gray-700 text-white h-8 text-sm"
                            value={meta.description}
                            placeholder="Optional description (shown under the title)"
                            onChange={(e) => setMeta(f.path, { description: e.target.value })}
                            data-testid={`input-file-desc-${f.name}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    {selected.size} new file{selected.size === 1 ? "" : "s"} selected
                    {isFinalCreate && carryOverFileIds.size > 0
                      ? ` · ${carryOverFileIds.size} carried from old Final`
                      : ""}
                  </span>
                  <Button
                    disabled={
                      (selected.size === 0 && !(isFinalCreate && activeFinalShare && finalReplaceConfirmed && carryOverFileIds.size > 0)) ||
                      createMutation.isPending ||
                      replaceFinalMutation.isPending ||
                      creationBlockedByDownloads
                    }
                    onClick={() => {
                      if (isFinalCreate && activeFinalShare && finalReplaceConfirmed) {
                        replaceFinalMutation.mutate();
                      } else {
                        createMutation.mutate();
                      }
                    }}
                    style={isFinalCreate ? { background: '#34d391', color: '#0a1a12' } : undefined}
                    className={isFinalCreate ? "hover:opacity-90" : undefined}
                    data-testid="button-create-share"
                  >
                    {(createMutation.isPending || replaceFinalMutation.isPending)
                      ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      : <Link2 className="h-4 w-4 mr-1" />}
                    {isFinalCreate && activeFinalShare
                      ? "Replace Final & create link"
                      : `Create ${createTypeLabel} share link`}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        </div>
      </DialogContent>
      {sendLinkOpen && shareEmailDraft && effectiveProjectId && (
        <MailOutDialog
          open={sendLinkOpen}
          onOpenChange={setSendLinkOpen}
          projectId={effectiveProjectId}
          initialDraft={shareEmailDraft}
          composeLabel={`${shareTypeLabel(manageShareType)} Link`}
        />
      )}
    </Dialog>
  );
}

export default PreviewShareDialog;
